import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import type {
  AppNotification,
  AuditLog,
  LowSpaceAlert,
  RemainingSpaceBox,
  Statistics,
  User,
} from '../types';

// ---- Statistics / dashboard ------------------------------------------------
export function useStatistics() {
  return useQuery<Statistics>({
    queryKey: ['statistics'],
    queryFn: async () => (await api.get('/cryopreservation/stats')).data,
  });
}

export function useRemainingSpace() {
  return useQuery<RemainingSpaceBox[]>({
    queryKey: ['remaining-space'],
    queryFn: async () => (await api.get('/cryopreservation/remaining-space')).data,
  });
}

export function useLowSpaceAlerts() {
  return useQuery<LowSpaceAlert[]>({
    queryKey: ['low-space-alerts'],
    queryFn: async () => (await api.get('/cryopreservation/alerts')).data,
  });
}

export interface BoxOccupancy {
  box_number: string;
  capacity: number;
  used: number;
  remaining_space: number;
  occupied: { position: string; record_id: string; cell_line_name?: string | null }[];
}

export function useBoxOccupancy(boxNumber: string | null, excludeRecordId?: string) {
  return useQuery<BoxOccupancy>({
    queryKey: ['box-occupancy', boxNumber, excludeRecordId ?? null],
    enabled: !!boxNumber,
    queryFn: async () =>
      (
        await api.get('/cryopreservation/box-occupancy', {
          params: {
            box_number: boxNumber,
            ...(excludeRecordId ? { exclude_record_id: excludeRecordId } : {}),
          },
        })
      ).data,
  });
}

// ---- Audit trail -----------------------------------------------------------
export function useAuditLogs(page = 1, limit = 100) {
  return useQuery<AuditLog[]>({
    queryKey: ['audit-logs', page, limit],
    queryFn: async () =>
      (await api.get('/cryopreservation/audit-logs', { params: { page, limit } })).data,
  });
}

export function useVerifyAuditChain() {
  return useMutation({
    mutationFn: async () => (await api.get('/cryopreservation/audit-logs/verify')).data,
  });
}

// ---- Admin / developer tools ------------------------------------------------
// Dev-only: wipes records, box occupancy, audit logs and notifications.
// User accounts are preserved. The backend rejects this in production.
export function useClearDatabase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post('/cryopreservation/admin/clear-database')).data,
    onSuccess: () => qc.invalidateQueries(),
  });
}

// ---- Users -----------------------------------------------------------------
export function useUsers() {
  return useQuery<User[]>({
    queryKey: ['users'],
    queryFn: async () => (await api.get('/auth/users')).data,
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) =>
      (await api.post(`/auth/users/${id}/role`, { role })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useAssignApprover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, approver_email }: { id: string; approver_email: string | null }) =>
      (await api.post(`/auth/users/${id}/approver`, { approver_email })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// ---- Notifications ---------------------------------------------------------
export function useNotifications() {
  return useQuery<AppNotification[]>({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get('/notifications')).data,
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post('/notifications/mark-all-read')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}
